import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ProgressBar } from 'react-native-paper';

import type { SocialLearningABTest } from '../../store/slices/socialLearningAnalyticsSlice';

interface ABTestParticipationCardProps {
  test: SocialLearningABTest;
  onOptOut: () => void;
  onViewResults: () => void;
}

export const ABTestParticipationCard: React.FC<
  ABTestParticipationCardProps
> = ({ test, onOptOut, onViewResults }) => {
  const [showDetails, setShowDetails] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return '#4CAF50';
      case 'paused':
        return '#FF9800';
      case 'completed':
        return '#2196F3';
      case 'cancelled':
        return '#F44336';
      default:
        return '#666';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return 'play-circle';
      case 'paused':
        return 'pause-circle';
      case 'completed':
        return 'checkmark-circle';
      case 'cancelled':
        return 'close-circle';
      default:
        return 'help-circle';
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.95) return '#4CAF50';
    if (confidence >= 0.8) return '#8BC34A';
    if (confidence >= 0.6) return '#FF9800';
    return '#F44336';
  };

  const handleOptOut = () => {
    Alert.alert(
      'Opt Out of Test',
      `Are you sure you want to opt out of "${test.name}"? This will remove you from the experiment and you may miss out on potential improvements.`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Opt Out', style: 'destructive', onPress: onOptOut },
      ]
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const calculateProgress = () => {
    if (test.status === 'completed') return 1;
    if (!test.endDate) return test.currentParticipants / test.sampleSize;

    const start = new Date(test.startDate).getTime();
    const end = new Date(test.endDate).getTime();
    const now = Date.now();

    return Math.min((now - start) / (end - start), 1);
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.titleContainer}>
          <Text style={styles.title}>{test.name}</Text>
          <View style={styles.statusContainer}>
            <Ionicons
              name={getStatusIcon(test.status) as any}
              size={16}
              color={getStatusColor(test.status)}
            />
            <Text
              style={[
                styles.statusText,
                { color: getStatusColor(test.status) },
              ]}
            >
              {test.status.toUpperCase()}
            </Text>
          </View>
        </View>
        <TouchableOpacity
          style={styles.detailsButton}
          onPress={() => setShowDetails(!showDetails)}
        >
          <Ionicons
            name={showDetails ? 'chevron-up' : 'chevron-down'}
            size={20}
            color='#666'
          />
        </TouchableOpacity>
      </View>

      {/* Description */}
      <Text style={styles.description}>{test.description}</Text>

      {/* Progress */}
      <View style={styles.progressSection}>
        <View style={styles.progressHeader}>
          <Text style={styles.progressLabel}>Test Progress</Text>
          <Text style={styles.progressValue}>
            {test.currentParticipants} / {test.sampleSize} participants
          </Text>
        </View>
        <ProgressBar
          progress={calculateProgress()}
          color='#2196F3'
          style={styles.progressBar}
        />
      </View>

      {/* Key Metrics */}
      <View style={styles.metricsSection}>
        <Text style={styles.metricsTitle}>Target Metrics</Text>
        <View style={styles.metricsList}>
          {test.targetMetrics.slice(0, 3).map((metric, index) => (
            <View key={index} style={styles.metricItem}>
              <Ionicons name='analytics' size={14} color='#666' />
              <Text style={styles.metricText}>{metric}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* Variants */}
      <View style={styles.variantsSection}>
        <Text style={styles.variantsTitle}>Test Variants</Text>
        {test.variants.map((variant, index) => (
          <View key={variant.id} style={styles.variantItem}>
            <View style={styles.variantHeader}>
              <Text style={styles.variantName}>{variant.name}</Text>
              <Text style={styles.variantAllocation}>
                {Math.round(variant.trafficAllocation * 100)}%
              </Text>
            </View>
            <Text style={styles.variantDescription}>{variant.description}</Text>
            <Text style={styles.variantParticipants}>
              {variant.participants} participants
            </Text>
          </View>
        ))}
      </View>

      {/* Detailed Information */}
      {showDetails && (
        <View style={styles.detailsSection}>
          <View style={styles.hypothesisSection}>
            <Text style={styles.hypothesisTitle}>Hypothesis</Text>
            <Text style={styles.hypothesisText}>{test.hypothesis}</Text>
          </View>

          <View style={styles.datesSection}>
            <View style={styles.dateItem}>
              <Ionicons name='calendar-outline' size={16} color='#666' />
              <Text style={styles.dateLabel}>Started:</Text>
              <Text style={styles.dateValue}>{formatDate(test.startDate)}</Text>
            </View>
            {test.endDate && (
              <View style={styles.dateItem}>
                <Ionicons name='calendar-outline' size={16} color='#666' />
                <Text style={styles.dateLabel}>Ends:</Text>
                <Text style={styles.dateValue}>{formatDate(test.endDate)}</Text>
              </View>
            )}
          </View>

          {test.results && (
            <View style={styles.resultsSection}>
              <Text style={styles.resultsTitle}>Preliminary Results</Text>
              <View style={styles.confidenceContainer}>
                <Text style={styles.confidenceLabel}>Confidence Level:</Text>
                <Text
                  style={[
                    styles.confidenceValue,
                    { color: getConfidenceColor(test.confidence) },
                  ]}
                >
                  {Math.round(test.confidence * 100)}%
                </Text>
              </View>
              <View style={styles.significanceContainer}>
                <Ionicons
                  name={
                    test.statisticalSignificance
                      ? 'checkmark-circle'
                      : 'close-circle'
                  }
                  size={16}
                  color={test.statisticalSignificance ? '#4CAF50' : '#F44336'}
                />
                <Text style={styles.significanceText}>
                  {test.statisticalSignificance
                    ? 'Statistically Significant'
                    : 'Not Yet Significant'}
                </Text>
              </View>
            </View>
          )}
        </View>
      )}

      {/* Actions */}
      <View style={styles.actionsSection}>
        {test.status === 'completed' && test.results ? (
          <TouchableOpacity
            style={styles.viewResultsButton}
            onPress={onViewResults}
          >
            <Ionicons name='bar-chart' size={16} color='#2196F3' />
            <Text style={styles.viewResultsButtonText}>View Full Results</Text>
          </TouchableOpacity>
        ) : (
          <View style={styles.activeActions}>
            <TouchableOpacity
              style={styles.optOutButton}
              onPress={handleOptOut}
            >
              <Ionicons name='exit-outline' size={16} color='#F44336' />
              <Text style={styles.optOutButtonText}>Opt Out</Text>
            </TouchableOpacity>
            <View style={styles.participationInfo}>
              <Ionicons
                name='information-circle-outline'
                size={16}
                color='#666'
              />
              <Text style={styles.participationText}>
                You're helping improve the platform!
              </Text>
            </View>
          </View>
        )}
      </View>

      {/* Privacy Notice */}
      <View style={styles.privacySection}>
        <Ionicons name='shield-checkmark-outline' size={14} color='#666' />
        <Text style={styles.privacyText}>
          Your data is anonymized and used only for this experiment
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  titleContainer: {
    flex: 1,
  },
  title: {
    fontSize: 16,
    fontWeight: '700',
    color: '#333',
    marginBottom: 4,
  },
  statusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },
  detailsButton: {
    padding: 4,
  },
  description: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
    marginBottom: 16,
  },
  progressSection: {
    marginBottom: 16,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  progressLabel: {
    fontSize: 14,
    color: '#666',
  },
  progressValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  progressBar: {
    height: 6,
    borderRadius: 3,
    backgroundColor: '#f0f0f0',
  },
  metricsSection: {
    marginBottom: 16,
  },
  metricsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  metricsList: {
    gap: 6,
  },
  metricItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  metricText: {
    fontSize: 13,
    color: '#666',
    marginLeft: 8,
    textTransform: 'capitalize',
  },
  variantsSection: {
    marginBottom: 16,
  },
  variantsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  variantItem: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  variantHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  variantName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  variantAllocation: {
    fontSize: 12,
    color: '#666',
    backgroundColor: '#e3f2fd',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 10,
  },
  variantDescription: {
    fontSize: 13,
    color: '#666',
    marginBottom: 4,
  },
  variantParticipants: {
    fontSize: 12,
    color: '#999',
  },
  detailsSection: {
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
    paddingTop: 16,
    marginBottom: 16,
  },
  hypothesisSection: {
    marginBottom: 16,
  },
  hypothesisTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 6,
  },
  hypothesisText: {
    fontSize: 13,
    color: '#666',
    lineHeight: 18,
  },
  datesSection: {
    marginBottom: 16,
  },
  dateItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  dateLabel: {
    fontSize: 13,
    color: '#666',
    marginLeft: 8,
    marginRight: 8,
  },
  dateValue: {
    fontSize: 13,
    fontWeight: '600',
    color: '#333',
  },
  resultsSection: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
  },
  resultsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  confidenceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  confidenceLabel: {
    fontSize: 13,
    color: '#666',
    marginRight: 8,
  },
  confidenceValue: {
    fontSize: 13,
    fontWeight: '600',
  },
  significanceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  significanceText: {
    fontSize: 13,
    color: '#666',
    marginLeft: 6,
  },
  actionsSection: {
    marginBottom: 12,
  },
  viewResultsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e3f2fd',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  viewResultsButtonText: {
    color: '#2196F3',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  activeActions: {
    gap: 12,
  },
  optOutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffebee',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  optOutButtonText: {
    color: '#F44336',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  participationInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
  },
  participationText: {
    fontSize: 13,
    color: '#666',
    marginLeft: 6,
    fontStyle: 'italic',
  },
  privacySection: {
    flexDirection: 'row',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
    paddingTop: 12,
  },
  privacyText: {
    fontSize: 11,
    color: '#666',
    marginLeft: 6,
  },
});
